// Phase 5Z.41 — Zentraler Hints-Katalog für alle Module.
// Stellt konsistente Erklärungen ("Was/Wozu/Verbunden mit") für jedes Modul bereit.
// Wird via <ModuleHint moduleKey="..."/> oder direkt mit HelpHint genutzt.
import HelpHint from "./HelpHint";

const HINTS = {
  // ─── Übersicht ────────────────────────────────────────────────────────────
  dashboard: {
    title: "Was sehe ich auf dem Dashboard?",
    content: (
      <>
        <p>Die <b className="text-amber-300">Kommandozentrale</b> ist Ihre Live-Übersicht für den ganzen Betrieb.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Aktive Fahrer / Fahrzeuge</b>: aus dem Mitarbeiter- und Fuhrpark-Stamm.</li>
          <li><b>Heute erfasst</b>: Zeiteinträge, die über das Fahrer-Portal gemeldet wurden.</li>
          <li><b>Genehmigungs-Queue</b>: offene Abwesenheiten · Profilanfragen · Zeit-Anfragen — direkt klickbar.</li>
          <li><b>Pickerl/Vignette/Garantie</b> zeigt KFZ-Termine in 30 Tagen — Daten aus dem Fuhrpark.</li>
          <li><b>Finanzen</b>: offene und überfällige Rechnungen aus der Buchhaltung.</li>
        </ul>
      </>
    ),
  },
  heute: {
    title: "Wie funktioniert die Tagesansicht?",
    content: (
      <>
        <p>„Heute" ist die <b className="text-amber-300">Operative Echtzeit-Sicht</b> auf den laufenden Tag.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Im Dienst</b>: Fahrer mit aktivem Zeiteintrag heute (klickbar → Mitarbeiter-Profil).</li>
          <li><b>Fehlend</b>: Fahrer im Dienstplan, aber ohne Zeiteintrag — Telefon-Icon ruft direkt an.</li>
          <li><b>Abwesend</b>: Heute genehmigte Urlaube/Krankenstände aus dem Modul Abwesenheiten.</li>
          <li><b>Fahrzeuge unterwegs</b>: laut KFZ-Übergaben (Modul Übergaben) noch nicht zurückgegeben.</li>
          <li><b>Übergaben heute</b>: Einträge aus Übergaben mit heutigem Datum.</li>
          <li><b>Aufgaben fällig</b>: alle offenen Aufgaben mit Fälligkeitsdatum heute.</li>
        </ul>
      </>
    ),
  },

  // ─── Operations ───────────────────────────────────────────────────────────
  zeiterfassung: {
    title: "Wie funktioniert die Zeiterfassung?",
    content: (
      <>
        <p>Hier werden <b className="text-amber-300">Arbeits- und Lenkzeiten pro Tag</b> erfasst und freigegeben.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Bulk-Import</b>: Excel pro Mitarbeiter und Monat — ideal für Nachbuchungen.</li>
          <li><b>Beifahrer-Modus</b>: für 2-Personen-Touren — kein eigenes Lenken, kein KM relevant.</li>
          <li><b>Pausen LP/RP/SAZ</b>: automatisches Lenkzeit-Ergebnis — verbunden mit Lenkprotokoll.</li>
          <li><b>Status-Workflow</b>: <code className="text-emerald-400">offen → geprüft → freigegeben</code> oder <code className="text-rose-400">abgelehnt</code>. Nur freigegebene fließen in die Lohnabrechnung.</li>
          <li><b>Portal-Badge</b>: Eintrag wurde vom Fahrer per Fahrer-Portal eingereicht.</li>
          <li><b>Auto-Vorschläge</b>: System schlägt letztes Fahrzeug, Start-KM und geplante Schicht vor.</li>
        </ul>
      </>
    ),
  },
  uebergaben: {
    title: "Wie funktionieren KFZ-Übergaben?",
    content: (
      <>
        <p>Dokumentiert <b className="text-amber-300">Fahrzeug-Übergabe und -Rücknahme</b> mit Foto und Unterschrift.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Übergabe</b>: Fahrzeug an Fahrer ausgegeben — Pflichtfelder: KM-Stand, Tank, Schlüssel.</li>
          <li><b>Rücknahme</b>: zurückgegeben — neue Schäden mit Foto dokumentieren.</li>
          <li><b>Schäden</b>: erscheinen automatisch im Fuhrpark-Profil.</li>
          <li><b>Fahrzeuge draußen</b>: Übergabe ohne passende Rücknahme → wird auf „Heute" angezeigt.</li>
          <li><b>Unterschrift</b>: Touch-fähig — funktioniert auch im Fahrer-Portal.</li>
        </ul>
      </>
    ),
  },
  lenkprotokoll: {
    title: "Was ist das Lenkprotokoll?",
    content: (
      <>
        <p>Das offizielle <b className="text-amber-300">Lenkzeit-Tagesprotokoll</b> für Behörden (z. B. § 24 Sek. AETR).</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Wird <b>automatisch</b> aus den Zeiterfassungs-Einträgen mit ausgefüllten Lenkzeiten generiert.</li>
          <li><b>Export</b>: PDF (Druckansicht) + CSV für Behörden-Anfragen.</li>
          <li><b>Verbunden mit</b>: Zeiterfassung (Quelle), Mitarbeiter (Stammdaten), Lenkerauskünfte (für Strafzettel-Antwort).</li>
        </ul>
      </>
    ),
  },
  dienstplan: {
    title: "Wie nutze ich den Dienstplan?",
    content: (
      <>
        <p>Wochen-/Monats-Schichtplan pro Mitarbeiter — die <b className="text-amber-300">Sollvorgabe</b> für die Zeiterfassung.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Schicht-Typen</b>: Tag · Nacht · Frei · Urlaub · Krank · Homeoffice — Farb-codiert.</li>
          <li><b>Zeiterfassung</b> liest Schicht + Einsatzort als Standardwert.</li>
          <li><b>Heute-Modul</b> vergleicht Plan ↔ tatsächliche Zeiterfassung → meldet „Fehlend".</li>
          <li><b>Bulk-Editor</b>: ganze Wochen/Mitarbeiter mit wenigen Klicks befüllen.</li>
        </ul>
      </>
    ),
  },
  lenkerauskunft: {
    title: "Wie funktionieren Lenkerauskünfte?",
    content: (
      <>
        <p>Antwortet auf <b className="text-amber-300">Strafverfahren-Anfragen</b> der Polizei: „Wer fuhr am Tag X um Uhrzeit Y das Fahrzeug Z?".</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Auto-Fill</b>: System sucht aus Zeiterfassung + Übergaben den Fahrer zum Datum/Uhrzeit.</li>
          <li><b>OCR</b>: gescanntes Anfrage-PDF wird per Gemini Vision analysiert — Aktenzahl, Datum, Kennzeichen extrahiert.</li>
          <li><b>Antwort-PDF</b>: offizielles Schreiben generieren + per E-Mail (Resend) versenden.</li>
          <li><b>Status-Tracking</b>: offen → beantwortet → archiviert.</li>
        </ul>
      </>
    ),
  },
  buchhaltung: {
    title: "Was macht das Buchhaltungs-Modul?",
    content: (
      <>
        <p>Ein- und Ausgangsrechnungen, OP-Liste und Mahnwesen — die <b className="text-amber-300">Finanz-Drehscheibe</b>.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Eingangsrechnungen</b>: per OCR scannen oder manuell anlegen.</li>
          <li><b>Ausgangsrechnungen</b>: aus Aufträgen / Stundennachweisen.</li>
          <li><b>OP-Liste</b>: offene Posten, überfällig markiert in Rot.</li>
          <li><b>Steuerberater-Portal</b>: read-only Sicht für externe Buchhalter (verknüpft mit Modul Steuerberater).</li>
          <li><b>Auf Heute</b>: offene + überfällige Rechnungen erscheinen im Finanzen-Widget.</li>
        </ul>
      </>
    ),
  },
  aufgaben: {
    title: "Wie funktionieren Aufgaben?",
    content: (
      <>
        <p>Aufgaben gehen an <b className="text-amber-300">Büro</b> (Disponent/Backoffice) oder konkrete <b className="text-amber-300">Fahrer</b>.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Office-Aufgabe ohne Person</b>: jeder mit Zugriff kann „Übernehmen".</li>
          <li><b>Fahrer-Aufgabe</b>: Fahrer-Portal blockt bis er Annehmen/Ablehnen klickt (Grund Pflicht).</li>
          <li><b>Abteilung</b>: filtert Sichtbarkeit (Disponent „Amazon" sieht nur Amazon-Aufgaben).</li>
          <li><b>Glocken-Counter</b> in der Sidebar zeigt offene Aufgaben in Ihrem Bereich.</li>
        </ul>
      </>
    ),
  },
  abwesenheiten: {
    title: "Wie funktionieren Abwesenheiten?",
    content: (
      <>
        <p>Erfasst <b className="text-amber-300">Urlaub, Krankenstand, Homeoffice, Sonderurlaub</b> mit Genehmigungs-Workflow.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Anfrage</b>: Mitarbeiter (oder Fahrer-Portal) reicht Antrag ein.</li>
          <li><b>Genehmigung</b>: Geschäftsführung/Backoffice prüft → genehmigt oder lehnt ab.</li>
          <li><b>Verbunden mit</b>: Lohnabrechnung (Tagesgeld-Sperre), Heute (Live-Anzeige), Dienstplan (überschreibt Schicht).</li>
          <li><b>Resturlaub</b>: aus Mitarbeiter-Stamm berechnet — Saldo pro Mitarbeiter sichtbar.</li>
        </ul>
      </>
    ),
  },
  profilanfragen: {
    title: "Was sind Profilanfragen?",
    content: (
      <>
        <p>Wenn ein Fahrer im <b className="text-amber-300">Fahrer-Portal</b> Stammdaten ändern will (Adresse, Telefon, Bank), kommt das hier zur Genehmigung.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Vorher/Nachher-Vergleich</b>: alle Änderungen sichtbar.</li>
          <li><b>Approve</b>: Daten werden in den Mitarbeiter-Stamm übernommen.</li>
          <li><b>Reject</b>: mit Grund — Fahrer sieht den Hinweis im Portal.</li>
          <li><b>Audit-Log</b>: jede Entscheidung ist ISO-27001-konform protokolliert.</li>
        </ul>
      </>
    ),
  },
  zeitAnfragen: {
    title: "Was sind Zeit-Anfragen?",
    content: (
      <>
        <p>Fahrer können im Portal nachträglich <b className="text-amber-300">Korrekturen oder Ergänzungen</b> für vergangene Zeiteinträge anfordern.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Beispiele</b>: vergessene Pause, Tippfehler bei KM, Pause-Zeit ändern.</li>
          <li><b>Approve</b>: System aktualisiert den ursprünglichen Zeiteintrag (mit Audit-Spur).</li>
          <li><b>Reject</b>: Fahrer sieht den Status im Portal.</li>
        </ul>
      </>
    ),
  },

  // ─── Stammdaten ───────────────────────────────────────────────────────────
  mitarbeiter: {
    title: "Was umfasst der Mitarbeiter-Stamm?",
    content: (
      <>
        <p>Die <b className="text-amber-300">zentrale Personalakte</b> — alle Module greifen hier auf Stammdaten zu.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Stammdaten + Vertrag</b>: Anstellung, Lohn, Eligibilities, Resturlaub.</li>
          <li><b>Lohn-Felder</b>: Regelgruppe + Vertragsstunden — Quelle für die Lohnabrechnung.</li>
          <li><b>Portal-Zugang</b>: Username + Passwort für Fahrer-Portal aktivieren/zurücksetzen.</li>
          <li><b>Dokumente</b>: Führerschein, Arbeitsvertrag, Bestätigungen — mit Ablauf-Erinnerung.</li>
          <li><b>Status</b>: <code className="text-emerald-400">aktiv</code>/<code className="text-zinc-400">inaktiv</code> — abgemeldete erscheinen standardmäßig nicht in Pickern.</li>
        </ul>
      </>
    ),
  },
  fuhrpark: {
    title: "Was ist im Fuhrpark enthalten?",
    content: (
      <>
        <p>Der <b className="text-amber-300">Fahrzeug-Stamm</b> mit allen Termin-Erinnerungen und Verträgen.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Termine</b>: Pickerl, §57a, Service, Vignette, Garantie — Ablauf in 30 Tagen taucht auf Heute auf.</li>
          <li><b>Schäden</b>: aus KFZ-Übergaben automatisch verlinkt.</li>
          <li><b>Karten</b>: zugewiesene Tank-/Maut-/Garagenkarten.</li>
          <li><b>Status</b>: aktiv/abgemeldet — abgemeldete erscheinen nicht in Pickern.</li>
        </ul>
      </>
    ),
  },
  karten: {
    title: "Was sind Karten?",
    content: (
      <>
        <p>Verwaltet <b className="text-amber-300">Tankkarten, Mautkarten, Garagenkarten</b> mit Ablaufdatum und Zuweisung.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Zuweisung</b>: an Fahrzeug oder Mitarbeiter — Verlauf wird protokolliert.</li>
          <li><b>Ablauf</b>: Erinnerung 30 Tage vorher auf dem Dashboard.</li>
          <li><b>PIN</b>: verschlüsselt gespeichert — sichtbar nur für berechtigte Rollen.</li>
        </ul>
      </>
    ),
  },
  dokumente: {
    title: "Wie funktioniert die Dokumenten-Verwaltung?",
    content: (
      <>
        <p>Zentrales <b className="text-amber-300">Dokumenten-Archiv</b> mit Vorlagen-Engine und automatischer Kategorisierung.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Verlinkung</b>: jedes Dokument hängt an Mitarbeiter, Fahrzeug, Karte, Auftrag oder Rechnung.</li>
          <li><b>Vorlagen</b>: Word-/PDF-Vorlagen mit Platzhaltern für Massenbriefe.</li>
          <li><b>Briefkopf</b>: zentral gepflegt im Firmenprofil.</li>
          <li><b>Ablauf-Erinnerung</b>: Dokumente mit Gültigkeit (z. B. Führerschein) erscheinen rechtzeitig auf dem Dashboard.</li>
        </ul>
      </>
    ),
  },
  finanzen: {
    title: "Wofür ist Finanzen?",
    content: (
      <>
        <p>Die <b className="text-amber-300">Cockpit-Sicht</b> auf alle Geldflüsse — verknüpft Buchhaltung, Aufträge und Lohnabrechnung.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Offene Posten</b>: Eingangs- und Ausgangsrechnungen mit Fälligkeit.</li>
          <li><b>Mahnwesen</b>: überfällige Rechnungen können Mahnstufen automatisch durchlaufen (geplant).</li>
          <li><b>Cash-Flow</b>: Übersicht je Monat / Auftraggeber.</li>
        </ul>
      </>
    ),
  },

  // ─── Werkzeuge ────────────────────────────────────────────────────────────
  exportCenter: {
    title: "Was ist das Export-Center?",
    content: (
      <>
        <p>Zentraler Ort für alle <b className="text-amber-300">Excel-, PDF- und CSV-Exporte</b>.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Lohn- &amp; Gehaltsabrechnung</b>: Amazon/UPS-Layout (Single-Sheet) oder DPD-Layout (1 Blatt pro MA).</li>
          <li><b>Lohnläufe</b>: gespeicherte Monats-Läufe mit Approval-Workflow (Draft → Approved → Locked).</li>
          <li><b>Datensätze exportieren</b>: roher Excel-Dump aus Mitarbeiter, Fahrzeuge, Aufgaben, Karten, Buchhaltung etc.</li>
          <li><b>Export-Historie</b>: jeder Export ist mit Benutzer, Filter und Datei-Hash protokolliert.</li>
        </ul>
      </>
    ),
  },
  payrollHub: {
    title: "Wie funktioniert die Lohnabrechnung?",
    content: (
      <>
        <p>Das <b className="text-amber-300">Lohn-Excel</b> wird live aus Zeiterfassung + Mitarbeiter-Stamm + Regelgruppen berechnet.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Regeln</b>: Rundung, Überstunden, Tagesgeld, Sa/So/Feiertag — alle aus den Regelgruppen pro Mitarbeiter.</li>
          <li><b>Layouts</b>: Amazon/UPS (Single-Sheet) oder DPD (1 Blatt pro MA).</li>
          <li><b>Override</b>: Legacy-Toggles ganz unten — nur für Notfall-Overrides.</li>
          <li><b>Verbunden mit</b>: Lohnläufe (Approval) · Settings → Lohnabrechnung (Regeln) · Steuerberater (Versand).</li>
        </ul>
      </>
    ),
  },
  payrollRuns: {
    title: "Wie funktionieren Lohnläufe?",
    content: (
      <>
        <p>Ein <b className="text-amber-300">Lohnlauf</b> ist ein gespeicherter Monats-Stand mit Approval-Workflow und Audit-Log.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Status-Maschine</b>: <code className="text-emerald-400">Draft → In Review → Approved → Exported → Locked</code>.</li>
          <li><b>Berechnung</b>: einmal beim Anlegen — bleibt eingefroren, auch wenn Zeiterfassung später noch geändert wird.</li>
          <li><b>Excel-Export</b>: Amazon/UPS oder DPD-Layout aus dem Detail-Modal.</li>
          <li><b>An Buchhaltung senden</b>: Excel(s) per E-Mail an den hinterlegten Steuerberater.</li>
          <li><b>Audit-Log</b>: jeder Status-Wechsel + Versand mit Benutzer + Begründung.</li>
        </ul>
      </>
    ),
  },
  payrollSettings: {
    title: "Wie konfiguriere ich die Lohn-Regeln?",
    content: (
      <>
        <p>Hier definieren Sie <b className="text-amber-300">Regelgruppen</b> (z. B. Allgemein, Amazon, UPS, DPD, Office) mit unterschiedlichen Berechnungen.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Arbeitszeit</b>: Std/Tag, Pause-Default, Missing-Entry-Verhalten.</li>
          <li><b>Überstunden</b>: tägl./wöchentl./monatl. — Schwelle + Auszahlungs-Modus.</li>
          <li><b>Rundung</b>: 9 Modi (auf 5/15/30/60 Min runden, threshold-basiert).</li>
          <li><b>Sa/So/Feiertag</b>: Bonus-% pro Tag + Bundesland für AT-Feiertage.</li>
          <li><b>Tagesgeld</b>: Inland-Tier oder fixer Betrag · Ausland-Sonderbetrag.</li>
          <li><b>Abwesenheits-Regeln</b>: pro Typ (Urlaub/Krank/...) — bezahlt? zählt zu Arbeit? affects Tagesgeld?</li>
          <li><b>Adjustments</b>: Vorschuss, Schaden, Strafe, Tankkarte — als Spalten im Export.</li>
          <li><b>Export-Vorlage</b>: Format + Spalten-Reihenfolge + -Sichtbarkeit.</li>
          <li><b>Approval</b>: welche Rollen dürfen Lohnläufe freigeben? Lock nach Export?</li>
          <li><b>Zuweisung</b>: Mitarbeiter werden Regelgruppen zugeordnet — pro Lohnabrechnung wird ihre Gruppe genommen.</li>
        </ul>
      </>
    ),
  },
  reports: {
    title: "Was zeigen die Berichte?",
    content: (
      <>
        <p>Aggregierte <b className="text-amber-300">KPI-Auswertungen</b> über alle Module — ohne Excel-Export, nur visuell.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Stunden pro Mitarbeiter</b>: Soll vs. Ist — Monatsvergleich.</li>
          <li><b>Fahrzeug-Auslastung</b>: KM/Std pro Fahrzeug.</li>
          <li><b>Abwesenheits-Heatmap</b>: Trends über das Jahr.</li>
          <li><b>Buchhaltung</b>: Umsatz, OP-Quote, Mahnstufen.</li>
        </ul>
      </>
    ),
  },
  audit: {
    title: "Was zeigt das Audit-Log?",
    content: (
      <>
        <p><b className="text-amber-300">ISO-27001-konformer</b> Nachweis aller Änderungen — wer hat wann was geändert?</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Jedes Create/Update/Delete in allen Modulen erscheint hier.</li>
          <li><b>Filter</b>: nach User, Modul, Aktion, Datum.</li>
          <li><b>Vorher/Nachher-Diff</b>: aufklappbar pro Eintrag.</li>
          <li><b>Aufbewahrung</b>: per Settings konfigurierbar (Standard: 7 Jahre).</li>
        </ul>
      </>
    ),
  },
  security: {
    title: "Was zeigt das Sicherheits-Dashboard?",
    content: (
      <>
        <p>Live-<b className="text-amber-300">Security-Cockpit</b>: Login-Versuche, fehlerhafte Authentifizierungen, Brute-Force-Erkennung.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Fehlgeschlagene Logins</b>: heute · letzte 7 Tage.</li>
          <li><b>Auto-Lockout</b>: nach X Fehlversuchen wird der User temporär gesperrt.</li>
          <li><b>Verdächtige IPs</b>: Top-Liste mit Geo-Information (best-effort).</li>
        </ul>
      </>
    ),
  },

  // ─── Profil/Einstellungen ─────────────────────────────────────────────────
  firmenprofil: {
    title: "Was ist im Firmenprofil?",
    content: (
      <>
        <p>Ihre <b className="text-amber-300">offizielle Firmen-Identität</b> — wird in PDFs, E-Mails, Briefkopf und Login-Seite verwendet.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Logo + Favicon</b>: für Login-Seite und Browser-Tab.</li>
          <li><b>Briefkopf</b>: pro Dokument-Typ (Rechnung, Lieferschein, Lenkerauskunft) konfigurierbar.</li>
          <li><b>E-Mail-Signaturen</b>: zentrale + modulspezifische Signaturen für ausgehende Mails.</li>
          <li><b>Bank, UID, FN</b>: erscheinen automatisch in Rechnungen und Mahnungen.</li>
        </ul>
      </>
    ),
  },
  einstellungen: {
    title: "Was kann ich in den Einstellungen?",
    content: (
      <>
        <p>Zentrale Konfiguration für <b className="text-amber-300">Rollen, Module, APIs und externe Partner</b>.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Rollen-Matrix</b>: granulare Rechte pro Modul × Aktion (Create/Read/Update/Delete/Approve).</li>
          <li><b>Module</b>: nicht benötigte Module ausblenden.</li>
          <li><b>APIs</b>: Resend (Mails), Gemini (OCR) — Keys hinterlegen.</li>
          <li><b>Steuerberater</b>: externer Buchhalter mit Portal-Zugang + Lohnverrechnungs-E-Mail.</li>
          <li><b>Lohnabrechnung</b>: Regelgruppen-Editor (siehe „Wie konfiguriere ich Lohn-Regeln?").</li>
        </ul>
      </>
    ),
  },
  steuerberater: {
    title: "Wie funktionieren Steuerberater?",
    content: (
      <>
        <p>Verknüpft <b className="text-amber-300">externe Buchhalter / Lohnverrechner</b> — sie bekommen optional ein read-only Portal.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Rolle-Tag</b>: <code className="text-emerald-400">lohnverrechnung</code> (E-Mail für Lohn-Versand) oder <code className="text-blue-400">buchhaltung</code> (Saldo) oder <code className="text-zinc-300">allgemein</code>.</li>
          <li><b>E-Mail Lohnabrechnung</b>: Default-Empfänger für „An Buchhaltung senden" aus den Lohnläufen.</li>
          <li><b>Portal-Zugang</b>: optional — generiert Login + sendet Welcome-Mail (read-only via Steuerberater-Portal).</li>
          <li><b>Primärer Steuerberater</b>: bei mehreren Einträgen wird der primäre als Default genommen.</li>
        </ul>
      </>
    ),
  },

  // ─── Super Admin ──────────────────────────────────────────────────────────
  tenants: {
    title: "Was ist die Mandanten-Verwaltung?",
    content: (
      <>
        <p>Super-Admin-Sicht auf alle <b className="text-amber-300">Tenants (Mandanten)</b> der SaaS-Plattform.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Mandant</b>: eigene Firma mit isolierten Daten (alle Module).</li>
          <li><b>Paket</b>: Basic/Pro/Enterprise — bestimmt sichtbare Module + Feature-Flags.</li>
          <li><b>Feature-Flags</b>: pro Tenant einzeln überschreiben (z. B. „Steuerberater-Portal aus").</li>
        </ul>
      </>
    ),
  },
  packages: {
    title: "Wie definiere ich Pakete?",
    content: (
      <>
        <p>Pakete legen fest, <b className="text-amber-300">welche Module ein Tenant nutzen darf</b>.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Module</b>: Mitarbeiter, Fuhrpark, Buchhaltung, Aufgaben usw. — pro Paket aktivierbar.</li>
          <li><b>Default-Feature-Flags</b>: Standardwerte für neue Tenants in diesem Paket.</li>
          <li>Beim Paket-Wechsel werden Module neu zugewiesen, Flags gemerged (Tenant-Override gewinnt).</li>
        </ul>
      </>
    ),
  },
  billing: {
    title: "Was zeigt die Abrechnungs-Übersicht?",
    content: (
      <>
        <p>Stripe-Abos, Rechnungen und Zahlungen <b className="text-amber-300">aller Tenants</b> auf einen Blick.</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Aktive Abos</b>: pro Tenant + Paket + monatliche Gebühr.</li>
          <li><b>Mahnung</b>: Tenants mit überfälligen Stripe-Rechnungen.</li>
          <li><b>Auto-Suspend</b>: optional bei mehrfachem Zahlungsausfall.</li>
        </ul>
      </>
    ),
  },
};

/** Render a centralised hint by moduleKey. Returns null if key is unknown. */
export default function ModuleHint({ moduleKey, defaultOpen = false, className = "mb-4" }) {
  const cfg = HINTS[moduleKey];
  if (!cfg) return null;
  return (
    <HelpHint
      title={cfg.title}
      defaultOpen={defaultOpen}
      className={className}
      testid={`help-${moduleKey}`}
    >
      {cfg.content}
    </HelpHint>
  );
}

export { HINTS };
